import {Injectable, inject, signal, computed} from '@angular/core';
import {Router} from "@angular/router";
import {LoginResponse, User} from "../models/user/user"
import { from, Observable} from "rxjs";
import {switchMap} from "rxjs/operators";
import {LocalstorageService} from "./localstorage.service";
import {SupabaseService} from "../core/apiSupabase.service";
import {environment} from "../../environments/environment";

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private localstorageService = inject(LocalstorageService);
    private supabaseService = inject(SupabaseService);
    private router = inject(Router);

    private readonly TOKEN_KEY = 'token-growphi';
    private readonly USER_DATA_KEY = 'userData-growphi';

    // --- SIGNALS
    public currentUser = signal<User | null | undefined>(undefined);

    // 2. Se llama al signal con `this.currentUser()` para obtener su valor.
    public authStatusChecked = computed(() => this.currentUser() !== undefined);

    public isLoggedIn = computed(() => !!this.currentUser());

    constructor() {
        this.checkAuthStatus();
    }


    // Método para cerrar sesión (Añadido para completar el servicio)
    logout() {
        this.localstorageService.removeItem(this.TOKEN_KEY);
        this.localstorageService.removeItem(this.USER_DATA_KEY);
        // Limpiar cache de roles antes de limpiar el usuario
        this._cachedRoles = null;
        this._lastUserId = null;
        this.currentUser.set(null);
        // AuthorizationService detectará automáticamente el cambio de usuario y limpiará su cache
        this.router.navigate(['/authentication']);
    }

    // Método para guardar la sesión
    private setSession(loginResponse: LoginResponse) {
        this.localstorageService.setItem(this.TOKEN_KEY, loginResponse.token);

        if (loginResponse.user) {
            this.localstorageService.setItem(this.USER_DATA_KEY, loginResponse.user);
            this.currentUser.set(loginResponse.user);
            
            // Navegación exitosa al dashboard
            this.router.navigate(['/dashboard']);
        }
    }

    // Verifica el estado de la sesión al iniciar la app
    private checkAuthStatus(): void {
        const user = this.localstorageService.getItem<User>(this.USER_DATA_KEY);
        this.currentUser.set(user); // user será el objeto User o null
    }

    public getToken(): string | null {
        return this.localstorageService.getItem<string>(this.TOKEN_KEY);
    }

    // Cache de roles para evitar ejecuciones repetidas
    private _cachedRoles: string[] | null = null;
    private _lastUserId: string | number | null = null;

    /**
     * Obtiene los roles del usuario actual (con cache)
     * Los roles se cachean y solo se recalculan si cambia el usuario
     */
    public getUserRoles(): string[] {
        const user = this.currentUser();
        const userId = user?.id;
        
        // Si el usuario cambió, invalidar cache
        if (userId !== this._lastUserId) {
            this._cachedRoles = null;
            this._lastUserId = userId || null;
        }
        
        // Si hay cache, retornarlo
        if (this._cachedRoles !== null) {
            return this._cachedRoles;
        }
        
        // Calcular roles una sola vez
        const roles = user?.roles || [];
        this._cachedRoles = roles;
        
        // Solo log en desarrollo y primera vez
        if (!environment.production && roles.length > 0) {
            console.log('👤 Usuario actual:', { id: user?.id, email: user?.email });
            console.log('🎭 Roles del usuario:', roles);
        }
        
        return roles;
    }

    /**
     * Verifica si el usuario tiene un rol específico
     */
    public hasRole(role: string): boolean {
        const userRoles = this.getUserRoles();
        return userRoles.includes(role);
    }

    /**
     * Verifica si el usuario tiene alguno de los roles especificados
     */
    public hasAnyRole(roles: string[]): boolean {
        const userRoles = this.getUserRoles();
        return roles.some(role => userRoles.includes(role));
    }



    // ============================================
    // MÉTODOS PARA SUPABASE (Nuevos - No reemplazan los existentes)
    // ============================================

    /**
     * Login con Supabase Auth
     * NO permite registro, solo login de usuarios creados por admin
     * @param credentials - Credenciales de login (email y password)
     * @returns Observable con los datos de la sesión
     */
    loginWithSupabase(credentials: { email: string; password: string }): Observable<any> {
        const supabase = this.supabaseService.getClient();
        
        return from(
            supabase.auth.signInWithPassword({
                email: credentials.email,
                password: credentials.password
            })
        ).pipe(
            switchMap(async (response) => {
                if (response.error) {
                    throw response.error;
                }

                // Obtener datos del usuario desde tabla users
                const userData = await this.fetchUserDataFromSupabase(response.data.user.id);
                
                this.setSessionWithSupabase({
                    token: response.data.session.access_token,
                    user: userData
                });

                return response.data;
            })
        );
    }

    /**
     * Logout con Supabase Auth
     * Cierra la sesión en Supabase y limpia el localStorage
     */
    async logoutWithSupabase(): Promise<void> {
        const supabase = this.supabaseService.getClient();
        await supabase.auth.signOut();
        
        this.localstorageService.removeItem(this.TOKEN_KEY);
        this.localstorageService.removeItem(this.USER_DATA_KEY);
        // Limpiar cache de roles antes de limpiar el usuario
        this._cachedRoles = null;
        this._lastUserId = null;
        this.currentUser.set(null);
        // AuthorizationService detectará automáticamente el cambio de usuario y limpiará su cache
        this.router.navigate(['/authentication']);
    }

    /**
     * Verifica el estado de autenticación con Supabase al iniciar la app
     * Obtiene la sesión activa de Supabase y carga los datos del usuario
     */
    async checkAuthStatusWithSupabase(): Promise<void> {
        const supabase = this.supabaseService.getClient();
        const { data: { session } } = await supabase.auth.getSession();

        if (session) {
            try {
                const userData = await this.fetchUserDataFromSupabase(session.user.id);
                this.currentUser.set(userData);
            } catch (error) {
                console.error('Error fetching user data:', error);
                this.currentUser.set(null);
            }
        } else {
            this.currentUser.set(null);
        }
    }

    /**
     * Obtiene los datos del usuario desde la tabla users de Supabase
     * @param userId - ID del usuario en Supabase Auth
     * @returns Promise con los datos del usuario mapeados al modelo User
     */
    private async fetchUserDataFromSupabase(userId: string): Promise<User> {
        const supabase = this.supabaseService.getClient();
        
        // Obtener datos de usuario
        const { data: userData, error: userError } = await supabase
            .from('users')
            .select('*')
            .eq('id', userId)
            .single();

        if (userError) {
            console.error('Error fetching user data:', userError);
            throw userError;
        }

        // Obtener roles por separado para evitar problemas con RLS en joins
        const { data: rolesData, error: rolesError } = await supabase
            .from('user_roles')
            .select('role')
            .eq('user_id', userId);

        if (rolesError) {
            console.error('Error fetching user roles:', rolesError);
            // No lanzar error, solo loguear - los roles pueden estar vacíos
        }

        return {
            id: userData.id,
            email: userData.email,
            name: userData.name,
            lastName: userData.last_name,
            phone: userData.phone,
            media: userData.media,
            enabled: userData.enabled,
            isDefaultPassword: userData.is_default_password,
            roles: rolesData?.map((r: any) => r.role) || []
        };
    }

    /**
     * Guarda la sesión después del login con Supabase
     * @param sessionData - Datos de la sesión (token y usuario)
     */
    private setSessionWithSupabase(sessionData: { token: string; user: User }): void {
        this.localstorageService.setItem(this.TOKEN_KEY, sessionData.token);
        this.localstorageService.setItem(this.USER_DATA_KEY, sessionData.user);
        this.currentUser.set(sessionData.user);

        // Redirigir al dashboard después del login
        this.router.navigate(['/dashboard']);
    }

    /**
     * Cambia la contraseña del usuario con Supabase Auth
     * Verifica la contraseña antigua antes de cambiar a la nueva
     * @param userId - ID del usuario (UUID string)
     * @param passwordData - Datos de contraseña (oldPassword, newPassword, newRetypedPassword)
     * @returns Observable que se completa cuando la contraseña se cambia exitosamente
     */
    changePasswordWithSupabase(userId: string, passwordData: { oldPassword: string; newPassword: string; newRetypedPassword: string }): Observable<void> {
        const supabase = this.supabaseService.getClient();
        const currentUser = this.currentUser();
        
        if (!currentUser?.email) {
            throw new Error('Usuario no encontrado');
        }

        return from(
            // Primero verificar que la contraseña antigua sea correcta
            supabase.auth.signInWithPassword({
                email: currentUser.email,
                password: passwordData.oldPassword
            })
        ).pipe(
            switchMap(async (authResponse) => {
                if (authResponse.error) {
                    throw new Error('Contraseña actual incorrecta');
                }

                // Si la contraseña antigua es correcta, actualizar a la nueva
                const updateResponse = await supabase.auth.updateUser({
                    password: passwordData.newPassword
                });

                if (updateResponse.error) {
                    throw updateResponse.error;
                }

                // Actualizar is_default_password = false en la tabla users
                const { error: updateError } = await supabase
                    .from('users')
                    .update({ is_default_password: false })
                    .eq('id', userId);

                if (updateError) {
                    console.error('Error actualizando is_default_password:', updateError);
                    // No lanzar error, la contraseña ya se cambió en Auth
                }

                // Actualizar el usuario en el estado local
                const updatedUser = await this.fetchUserDataFromSupabase(userId);
                this.localstorageService.setItem(this.USER_DATA_KEY, updatedUser);
                this.currentUser.set(updatedUser);

                return;
            })
        );
    }
}
